import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";

import { adsRepo } from "data/repositories/adsRepo";
import { adPlacements } from "data/admin/domain";
import { adminPaths } from "admin/paths";
import { normalizeSlug } from "admin/contentUtils";
import AdminFormActions from "components/admin/AdminFormActions";
import AdminPageHeader from "components/admin/AdminPageHeader";

function emptyAd() {
  return {
    id: "",
    title: "",
    subtitle: "",
    imageUrl: "",
    backgroundColor: "#1B4332",
    linkUrl: "",
    placement: "map",
    sortOrder: 1,
    isActive: true,
  };
}

function validateAdForm(form, isNew, adId) {
  const nextErrors = {};
  const normalizedId = form.id
    ? normalizeSlug(form.id)
    : normalizeSlug(`${form.placement}-${form.title}`) || `ad-${Date.now().toString(16)}`;

  if (!normalizedId) nextErrors.id = "Reklam ID üretilemedi.";
  if (!form.title.trim()) nextErrors.title = "Başlık zorunludur.";
  if (!form.placement) nextErrors.placement = "Yerleştirme zorunludur.";
  if (!isNew && normalizedId !== adId) nextErrors.id = "Mevcut kayıtta ID değiştirilemez.";

  return { nextErrors, normalizedId };
}

export default function AdEdit() {
  const navigate = useNavigate();
  const params = useParams();
  const adId = params.id;
  const isNew = adId === "new" || !adId;

  const [form, setForm] = useState(emptyAd());
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError("");
      if (isNew) {
        setForm(emptyAd());
        setErrors({});
        return;
      }
      try {
        const current = await adsRepo.getById(adId);
        if (cancelled) return;
        if (!current) {
          setError("Reklam bulunamadı");
          return;
        }
        setForm({
          ...emptyAd(),
          ...current,
          imageUrl: current.imageUrl || "",
          linkUrl: current.linkUrl || "",
        });
        setErrors({});
      } catch (loadError) {
        if (!cancelled) setError(loadError?.message || "Reklam yüklenemedi");
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [adId, isNew]);

  const onChange = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const url = await adsRepo.uploadImage(file, form.id || adId || "draft");
      setForm((prev) => ({ ...prev, imageUrl: url }));
    } catch (uploadError) {
      setError(uploadError?.message || "Görsel yüklenemedi. Storage kurallarını kontrol edin.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    setError("");
    const { nextErrors, normalizedId } = validateAdForm(form, isNew, adId);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const payload = {
      id: normalizedId,
      title: form.title.trim(),
      subtitle: String(form.subtitle || "").trim(),
      imageUrl: String(form.imageUrl || "").trim() || null,
      backgroundColor: form.backgroundColor || "#1B4332",
      linkUrl: String(form.linkUrl || "").trim(),
      placement: form.placement,
      sortOrder: Number(form.sortOrder) || 0,
      isActive: form.isActive !== false,
    };

    try {
      setSaving(true);
      if (isNew) {
        await adsRepo.create(payload);
      } else {
        await adsRepo.update(adId, payload);
      }
      navigate(`/admin${adminPaths.content.ads.list}`, {
        state: {
          status: {
            color: "success",
            message: `${payload.title} başarıyla ${isNew ? "oluşturuldu" : "güncellendi"}.`,
          },
        },
      });
    } catch (saveError) {
      setError(saveError?.message || "Kaydetme hatası");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AdminPageHeader
        gradient="warning"
        title={isNew ? "Yeni reklam" : "Reklam düzenle"}
        description="Mobil snap/slider reklamları. Görseli Firebase Storage'a yükleyin veya URL girin."
        backAction={() => navigate(`/admin${adminPaths.content.ads.list}`)}
      />

      <Container className="mt--6" fluid>
        <Row>
          <Col xl="8">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">Detay</h3>
              </CardHeader>
              <CardBody>
                {error ? <Alert color="danger">{error}</Alert> : null}
                <Form onSubmit={onSave}>
                  <FormGroup>
                    <Label>ID</Label>
                    <Input
                      value={form.id}
                      onChange={(e) => setForm((p) => ({ ...p, id: normalizeSlug(e.target.value) }))}
                      disabled={!isNew}
                      placeholder="Boş bırakırsanız otomatik üretilir"
                      invalid={Boolean(errors.id)}
                    />
                    <FormFeedback>{errors.id}</FormFeedback>
                  </FormGroup>

                  <FormGroup>
                    <Label>Yerleştirme (ekran)</Label>
                    <Input type="select" value={form.placement} onChange={onChange("placement")} invalid={Boolean(errors.placement)}>
                      {adPlacements.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Input>
                    <FormFeedback>{errors.placement}</FormFeedback>
                    <div className="small text-muted mt-1">Karadeniz sayfası = Harita / snap slider</div>
                  </FormGroup>

                  <FormGroup>
                    <Label>Başlık</Label>
                    <Input value={form.title} onChange={onChange("title")} invalid={Boolean(errors.title)} />
                    <FormFeedback>{errors.title}</FormFeedback>
                  </FormGroup>

                  <FormGroup>
                    <Label>Alt başlık</Label>
                    <Input value={form.subtitle} onChange={onChange("subtitle")} placeholder="Kısa açıklama" />
                  </FormGroup>

                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>Sıra</Label>
                        <Input type="number" value={form.sortOrder} onChange={onChange("sortOrder")} />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Arka plan rengi</Label>
                        <Input type="color" value={form.backgroundColor || "#1B4332"} onChange={onChange("backgroundColor")} />
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label>Görsel URL</Label>
                    <Input
                      value={form.imageUrl}
                      onChange={onChange("imageUrl")}
                      placeholder="https://... veya aşağıdan yükleyin"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Firebase Storage'a görsel yükle</Label>
                    <Input type="file" accept="image/*" onChange={onUpload} disabled={uploading || !adsRepo.usesFirebase()} />
                    <div className="small text-muted mt-1">
                      {uploading
                        ? "Yükleniyor..."
                        : adsRepo.usesFirebase()
                          ? "Dosya Storage'a gider, indirme URL'si forma yazılır."
                          : "Firebase .env yok; yalnızca URL kullanabilirsiniz."}
                    </div>
                  </FormGroup>

                  {form.imageUrl ? (
                    <div className="mb-3">
                      <img
                        src={form.imageUrl}
                        alt="Önizleme"
                        style={{ maxWidth: "100%", maxHeight: 180, borderRadius: 12, objectFit: "cover" }}
                      />
                    </div>
                  ) : null}

                  <FormGroup>
                    <Label>Tıklanınca gidecek link (opsiyonel)</Label>
                    <Input value={form.linkUrl} onChange={onChange("linkUrl")} placeholder="https://..." />
                  </FormGroup>

                  <div className="custom-control custom-checkbox mb-4">
                    <input
                      className="custom-control-input"
                      id="ad-active"
                      type="checkbox"
                      checked={form.isActive !== false}
                      onChange={onChange("isActive")}
                    />
                    <label className="custom-control-label" htmlFor="ad-active">
                      Aktif (mobilde göster)
                    </label>
                  </div>

                  {!isNew ? (
                    <div className="mb-3">
                      <Badge color={form.deletedAt ? "warning" : "success"} pill>
                        {form.deletedAt ? "Arşivde" : "Aktif kayıt"}
                      </Badge>
                    </div>
                  ) : null}

                  <AdminFormActions
                    saving={saving || uploading}
                    onCancel={() => navigate(`/admin${adminPaths.content.ads.list}`)}
                  />
                </Form>
              </CardBody>
            </Card>
          </Col>

          <Col xl="4">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">Mobil eşleme</h3>
              </CardHeader>
              <CardBody className="small text-muted">
                <ul className="pl-3 mb-0">
                  <li>Keşfet → placement: home</li>
                  <li>Karadeniz snap slider → map</li>
                  <li>Fırsatlar → deals</li>
                  <li>İşletmeler → businesses</li>
                  <li>Tüm ekranlar → all</li>
                </ul>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
